import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  companyDashboardFiltersService,
  COMPANY_DASHBOARD_EMPLOYEES_PAGE_SIZE,
  type CompanyDashboardSection,
  type CompanyDashboardCompany,
  type CompanyDashboardArea,
  type CompanyDashboardEmployee,
} from '@/api/domains/attendance/companyDashboard';
import { useAbortController } from '@/hooks/ui/useAbortController';
import { useUI } from '@/hooks/ui/useUI';
import { toStringArray } from '@/utils';


// Local (non-Redux) state backing the "Empresa" dashboard filters panel. Unlike
// "Mi equipo" (where [] means "todas las opciones"), companyIds/areaIds are seeded
// with every catalog id on load — the filter panel starts with "Todas las empresas"
// / "Todas las áreas" explicitly selected, per business rule. employeeIds keeps the
// "[] = todos" convention since there is no catalog id to seed it with upfront.
export interface CompanyDashboardFilterState {
  sectionCodes: string[];
  companyIds: number[];
  areaIds: number[];
  employeeIds: number[];
}

const DEFAULT_FILTER_STATE: CompanyDashboardFilterState = {
  sectionCodes: [],
  companyIds: [],
  areaIds: [],
  employeeIds: [],
};

type SelectOption = { value: string; label: string };

function toNumberArray(value: string | string[]): number[] {
  return toStringArray(value).map(Number).filter((n) => !Number.isNaN(n));
}

export interface UseCompanyDashboardFiltersResult {
  isLoading: boolean;
  isAreasLoading: boolean;
  isEmployeesLoading: boolean;
  // Colaboradores selector pagination (ASIS-5638 code review fix — real incremental
  // pagination instead of a single 1000-row load; same pattern as
  // useEmployeeDimensionalFilter's onLoadMore/hasNextPage/isLoadingMore).
  isLoadingMoreEmployees: boolean;
  employeeHasMore: boolean;
  loadMoreEmployees: () => Promise<void>;
  filters: CompanyDashboardFilterState;
  // Raw catalogs, exposed so sibling hooks/components can reuse them without
  // re-fetching (same convention as useMyTeamFilters' `sections`/`companies`).
  sections: CompanyDashboardSection[];
  companies: CompanyDashboardCompany[];
  areas: CompanyDashboardArea[];
  employees: CompanyDashboardEmployee[];
  catalogOptions: {
    sectionOptions: SelectOption[];
    companyOptions: SelectOption[];
    areaOptions: SelectOption[];
    employeeOptions: SelectOption[];
  };
  handlers: {
    onSectionChange: (codes: string | string[]) => void;
    onCompanyChange: (ids: string | string[]) => void;
    onAreaChange: (ids: string | string[]) => void;
    onEmployeeChange: (ids: string | string[]) => void;
  };
  refetch: () => void;
}

export function useCompanyDashboardFilters(): UseCompanyDashboardFiltersResult {
  const { t: translate } = useTranslation();
  const { showApiError } = useUI();
  const { createSignal: createFiltersSignal } = useAbortController();
  const { createSignal: createAreasSignal } = useAbortController();
  const { createSignal: createEmployeesSignal } = useAbortController();

  const [isLoading, setIsLoading] = useState(true);
  const [isAreasLoading, setIsAreasLoading] = useState(true);
  const [isEmployeesLoading, setIsEmployeesLoading] = useState(false);
  const [isLoadingMoreEmployees, setIsLoadingMoreEmployees] = useState(false);

  const [sections, setSections] = useState<CompanyDashboardSection[]>([]);
  const [companies, setCompanies] = useState<CompanyDashboardCompany[]>([]);
  const [areas, setAreas] = useState<CompanyDashboardArea[]>([]);
  const [employees, setEmployees] = useState<CompanyDashboardEmployee[]>([]);
  const [employeePage, setEmployeePage] = useState(1);
  const [employeeHasMore, setEmployeeHasMore] = useState(false);
  const [filters, setFilters] = useState<CompanyDashboardFilterState>(DEFAULT_FILTER_STATE);
  // Cache of selected employee details so they remain visible as Selector options even
  // when not present in the currently-loaded page(s) (same pattern as
  // useEmployeeDimensionalFilter's selectedEmployeeCache).
  const [selectedEmployeeCache, setSelectedEmployeeCache] =
    useState<Map<number, CompanyDashboardEmployee>>(new Map());

  // Mirror of filters.employeeIds so fetchEmployees can read the current selection
  // without listing it as a dependency (which would recreate the callback — and
  // retrigger the reload effect — on every selection change).
  const employeeIdsRef = useRef<number[]>(filters.employeeIds);
  useEffect(() => {
    employeeIdsRef.current = filters.employeeIds;
  }, [filters.employeeIds]);

  // Carga (inicial y bajo demanda) de secciones y empresas desde GET
  // /attendance/company-dashboard/filters. Siembra Secciones con la sección
  // `isDefault` del backend, y Empresa con TODAS las empresas recibidas (regla de
  // negocio: el panel arranca con "Todas las empresas" explícitamente marcada).
  const loadFilters = useCallback(() => {
    const signal = createFiltersSignal();
    setIsLoading(true);

    void (async () => {
      const response = await companyDashboardFiltersService.getFilters(signal);
      if (response.error?.code === 'ABORTED') return;
      setIsLoading(false);

      if (!response.success || !response.data) {
        showApiError(response, translate('attendance:dashboard.company.filters.loadError'));
        return;
      }

      const { sections: loadedSections, companies: loadedCompanies } = response.data;
      setSections(loadedSections);
      setCompanies(loadedCompanies);

      const defaultSection = loadedSections.find((section) => section.isDefault);
      setFilters((previous) => ({
        ...previous,
        sectionCodes: defaultSection ? [defaultSection.code] : [],
        companyIds: loadedCompanies.map((company) => company.id),
      }));
    })();
  }, [createFiltersSignal, showApiError, translate]);

  // Carga (inicial y bajo demanda) del catálogo de Área desde GET
  // /attendance/company-dashboard/filters/areas — SIN query params: depende de la
  // empresa de sesión, no de la selección del filtro Empresa, por lo que nunca se
  // recarga cuando ese filtro cambia. Siembra Área con TODAS las áreas recibidas
  // (mismo criterio que Empresa).
  const loadAreas = useCallback(() => {
    const signal = createAreasSignal();
    setIsAreasLoading(true);

    void (async () => {
      const response = await companyDashboardFiltersService.getAreas(signal);
      if (response.error?.code === 'ABORTED') return;
      setIsAreasLoading(false);

      if (!response.success || !response.data) {
        showApiError(response, translate('attendance:dashboard.company.filters.areasLoadError'));
        return;
      }

      setAreas(response.data);
      setFilters((previous) => ({
        ...previous,
        areaIds: response.data ? response.data.map((area) => area.id) : previous.areaIds,
      }));
    })();
  }, [createAreasSignal, showApiError, translate]);

  // Cargas iniciales al montar: filtros (secciones+empresas) y áreas en paralelo.
  useEffect(() => {
    loadFilters();
  }, [loadFilters]);

  useEffect(() => {
    loadAreas();
  }, [loadAreas]);

  // Carga (inicial y bajo demanda, paginada) del catálogo de Colaboradores desde GET
  // /attendance/company-dashboard/filters/employees. page === 1 reemplaza la lista
  // visible (recarga por cambio de Empresa/Área); page > 1 hace APPEND (scroll
  // infinito vía onLoadMore del Selector) — nunca poda ni reemplaza en ese caso.
  //
  // Cuando hay una selección de Colaboradores vigente (employeeIdsRef), la página 1
  // manda esos IDs al backend para que los valide contra TODO el universo
  // (Empresa+Área, sin paginar) y poda la selección contra `validatedSelection` de la
  // respuesta — nunca contra `items` (la página cargada), que es el bug original:
  // selecciones fuera de la primera página se perdían aunque siguieran vigentes.
  const fetchEmployees = useCallback(
    async (companyIds: number[], areaIds: number[], page: number) => {
      const isFirstPage = page === 1;
      const signal = createEmployeesSignal();
      if (isFirstPage) {
        setIsEmployeesLoading(true);
      } else {
        setIsLoadingMoreEmployees(true);
      }

      const currentSelection = employeeIdsRef.current;

      const shouldValidateSelection = isFirstPage && currentSelection.length > 0;

      try {
        const response = await companyDashboardFiltersService.getEmployees(
          {
            companyIds,
            areaIds,
            page,
            pageSize: COMPANY_DASHBOARD_EMPLOYEES_PAGE_SIZE,
            orderBy: 'employeeName',
            orderDirection: 'ASC',
            ...(shouldValidateSelection ? { employeeIds: currentSelection } : {}),
          },
          signal,
        );

        if (response.error?.code === 'ABORTED') return;

        if (!response.success || !response.data) {
          // Reset pagination so stale values don't keep enabling loadMore retries
          setEmployeeHasMore(false);
          showApiError(response, translate('attendance:dashboard.company.filters.employeesLoadError'));
          return;
        }

        const newEmployees = response.data.items;
        setEmployeeHasMore(response.data.pagination.hasNextPage);
        setEmployeePage(response.data.pagination.currentPage);

        if (isFirstPage) {
          setEmployees(newEmployees);

          if (currentSelection.length > 0) {
            // Prune against the backend-validated selection (evaluated against the
            // whole universe, not just page 1), so selections on later pages are
            // preserved when they still satisfy Empresa/Área.
            const validIds = new Set(response.data.validatedSelection);
            setFilters((previous) => ({
              ...previous,
              employeeIds: previous.employeeIds.filter((id) => validIds.has(id)),
            }));
          }
        } else {
          // Append next page, deduping by id to guard against overlap
          setEmployees((previous) => {
            const existingIds = new Set(previous.map((employee) => employee.id));
            const appended = newEmployees.filter((employee) => !existingIds.has(employee.id));
            return [...previous, ...appended];
          });
        }
      } finally {
        if (isFirstPage) {
          setIsEmployeesLoading(false);
        } else {
          setIsLoadingMoreEmployees(false);
        }
      }
    },
    [createEmployeesSignal, showApiError, translate],
  );

  const loadMoreEmployees = useCallback(async () => {
    if (isEmployeesLoading || isLoadingMoreEmployees || !employeeHasMore) return;
    await fetchEmployees(filters.companyIds, filters.areaIds, employeePage + 1);
  }, [
    isEmployeesLoading,
    isLoadingMoreEmployees,
    employeeHasMore,
    employeePage,
    filters.companyIds,
    filters.areaIds,
    fetchEmployees,
  ]);

  // Recalcula el catálogo de Colaboradores (desde la página 1) cada vez que cambian
  // Empresa y/o Área (incluida la siembra inicial de "todas las empresas/áreas" — a
  // diferencia de "Mi equipo", aquí no hay un catálogo de colaboradores previo que
  // reutilizar, así que la primera carga real también depende de este efecto). Se
  // espera a que ambos catálogos (empresas y áreas) terminen de cargar para evitar un
  // fetch intermedio con solo uno de los dos ya sembrado.
  useEffect(() => {
    if (isLoading || isAreasLoading) return;
    void fetchEmployees(filters.companyIds, filters.areaIds, 1);
  }, [filters.companyIds, filters.areaIds, isLoading, isAreasLoading, fetchEmployees]);

  // Purga el caché cuando selectedEmployeeIds se reduce externamente (poda por
  // validatedSelection tras un cambio de Empresa/Área).
  useEffect(() => {
    setSelectedEmployeeCache((previous) => {
      const selectedSet = new Set(filters.employeeIds);
      if ([...previous.keys()].every((id) => selectedSet.has(id))) return previous;
      const next = new Map(previous);
      for (const id of previous.keys()) {
        if (!selectedSet.has(id)) next.delete(id);
      }
      return next;
    });
  }, [filters.employeeIds]);

  const onSectionChange = useCallback((codes: string | string[]) => {
    setFilters((previous) => ({ ...previous, sectionCodes: toStringArray(codes) }));
  }, []);

  const onCompanyChange = useCallback((ids: string | string[]) => {
    setFilters((previous) => ({ ...previous, companyIds: toNumberArray(ids) }));
  }, []);

  const onAreaChange = useCallback((ids: string | string[]) => {
    setFilters((previous) => ({ ...previous, areaIds: toNumberArray(ids) }));
  }, []);

  const onEmployeeChange = useCallback((ids: string | string[]) => {
    const selected = toNumberArray(ids);
    setFilters((previous) => ({ ...previous, employeeIds: selected }));
    setSelectedEmployeeCache((previous) => {
      const selectedSet = new Set(selected);
      const next = new Map(previous);
      // Add newly selected employees visible in the currently-loaded page(s)
      for (const employee of employees) {
        if (selectedSet.has(employee.id)) {
          next.set(employee.id, employee);
        }
      }
      // Remove deselected employees
      for (const id of previous.keys()) {
        if (!selectedSet.has(id)) next.delete(id);
      }
      return next;
    });
  }, [employees]);

  const sectionOptions = useMemo<SelectOption[]>(
    () => sections.map((section) => ({ value: section.code, label: section.label })),
    [sections],
  );

  const companyOptions = useMemo<SelectOption[]>(
    () => companies.map((company) => ({ value: String(company.id), label: company.name })),
    [companies],
  );

  const areaOptions = useMemo<SelectOption[]>(
    () => areas.map((area) => ({ value: String(area.id), label: area.name })),
    [areas],
  );

  // Merges the selected-employee cache (may include employees outside the
  // currently-loaded page(s)) with the currently-loaded page(s) — the latter
  // overwrites the former when both are present — then sorts alphabetically (same
  // merge/sort convention as useEmployeeDimensionalFilter's employeeOptions).
  const employeeOptions = useMemo<SelectOption[]>(() => {
    const merged = new Map<number, SelectOption>();
    for (const employee of selectedEmployeeCache.values()) {
      merged.set(employee.id, { value: String(employee.id), label: employee.name });
    }
    for (const employee of employees) {
      merged.set(employee.id, { value: String(employee.id), label: employee.name });
    }
    return [...merged.values()].sort((a, b) => a.label.localeCompare(b.label, 'es'));
  }, [employees, selectedEmployeeCache]);

  const refetch = useCallback(() => {
    loadFilters();
    loadAreas();
  }, [loadFilters, loadAreas]);

  return {
    isLoading,
    isAreasLoading,
    isEmployeesLoading,
    isLoadingMoreEmployees,
    employeeHasMore,
    loadMoreEmployees,
    filters,
    sections,
    companies,
    areas,
    employees,
    catalogOptions: {
      sectionOptions,
      companyOptions,
      areaOptions,
      employeeOptions,
    },
    handlers: {
      onSectionChange,
      onCompanyChange,
      onAreaChange,
      onEmployeeChange,
    },
    refetch,
  };
}

export default useCompanyDashboardFilters;
